import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import type { UserCourseExercise, UserCourseLibraryExercise, UserCourseLibraryExercises } from "api/schemas/UserCourse";
import { useGetExercises } from "api/services/content/hooks";
import { useProfileContext } from "contexts/ProfileContext";
import type { LocationState } from "routes/library/views/Confirmation";
import { LibraryTablePagination } from "routes/patients/PatientProfile/components/UserCourse/components/LibraryTablePagination";
import { useLibraryListSettings } from "routes/patients/PatientProfile/components/UserCourse/utils/useLibraryListSettings";
import { LibraryItemSkeleton } from "shared/atoms/SkeletonLayout/components/LibraryItemSkeleton";
import { sortAlphabetically } from "utils/array/sort";
import { useTargetJointOptions } from "utils/form";
import type { LibraryListSettingsStorageKey } from "utils/storage/storageConfig";

import { ExerciseLibraryItem } from "./ExerciseLibraryItem";
import type { TypeOption } from "./LibraryHeader";
import { LibraryHeader } from "./LibraryHeader";

interface ExercisePickerLibraryProps {
  initialSelectedIds: number[];
  setError: (msg: string | null) => void;
  multiSelect?: boolean;
  onMoreInfoClick?: (exercise: UserCourseLibraryExercise) => void;
  onSelectedExercisesChange?: (exercises: UserCourseLibraryExercises) => void;
  onClick?: (id: number, exercise?: UserCourseExercise | UserCourseLibraryExercise) => void;
  selectedExercises?: UserCourseLibraryExercise[];
  libraryListSettingsStorageKey?: LibraryListSettingsStorageKey;
  persistLibraryListSettings?: boolean;
}

const existsInSelectedExercises = (selectedExercises: UserCourseLibraryExercise[], item: UserCourseLibraryExercise) =>
  !!selectedExercises.find(e => e.exercise_id === item.exercise_id);

const filterExercises =
  (selectedTypeOption: TypeOption, initialSelectedIds: number[]) => (item: UserCourseLibraryExercise) => {
    const added = initialSelectedIds.includes(item.exercise_id);
    if ((selectedTypeOption === "added" && !added) || (selectedTypeOption === "new" && added)) {
      return false;
    }
    return true;
  };

export const ExerciseLibraryList = ({
  initialSelectedIds,
  setError,
  multiSelect,
  onClick,
  onMoreInfoClick,
  onSelectedExercisesChange,
  selectedExercises = [],
  libraryListSettingsStorageKey,
  persistLibraryListSettings,
}: ExercisePickerLibraryProps) => {
  const {
    setSearchTerm,
    debouncedSearchTerm,
    equipment,
    setEquipment,
    movement,
    setMovement,
    position,
    setPosition,
    previewJoint,
    setPreviewJoint,
    selectedSort,
    setSelectedSort,
    limit,
    selectedTypeOption,
    setSelectedTypeOption,
    currentPage,
    setCurrentPage,
  } = useLibraryListSettings(libraryListSettingsStorageKey, persistLibraryListSettings);
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const { state } = useLocation() as { state: LocationState };
  const [filteredExercises, setFilteredExercises] = useState<UserCourseLibraryExercises>([]);
  const [locale] = useState(profile?.locale || "en-US");
  const targetJointOptions = useTargetJointOptions();

  const {
    refetch: refetchExercises,
    data,
    error: isError,
    isLoading,
  } = useGetExercises({
    joint: previewJoint !== "all" ? previewJoint : undefined,
    limit,
    locale,
    equipment: equipment.length > 0 ? equipment : undefined,
    movement: movement.length > 0 ? movement : undefined,
    page: currentPage + 1,
    position: position.length > 0 ? position : undefined,
    search: debouncedSearchTerm || undefined,
  });

  const allExercises = data?.items || [];
  const totalCount = data?.metadata?.total_count || 0;

  useEffect(() => {
    if (onSelectedExercisesChange) {
      onSelectedExercisesChange(selectedExercises);
    }
  }, [selectedExercises]);

  useEffect(() => {
    refetchExercises();

    return () => {
      // Clear location state when unmounting to avoid showing "new" badge on next visit
      window.history.replaceState({}, document.title);
    };
  }, []);

  useEffect(() => {
    if (isError) {
      setError(t("errors.generic"));
    } else {
      setError(null);
    }
  }, [isError, setError, t]);

  // generate filtered exercises
  useEffect(() => {
    if (!allExercises?.length) {
      setFilteredExercises([]);
    }
    if (allExercises?.length) {
      const fe = [...allExercises].filter(filterExercises(selectedTypeOption, initialSelectedIds));
      const sortedExercises = sortAlphabetically({
        list: fe,
        selectedSort,
        keySelector: exercise => exercise.levels[0].title,
      });

      if (state?.item && "exercise_id" in state.item) {
        // This is a hack to make sure that the newly created exercise always is at the top of the list and has the NEW badge.
        // In order to not risk duplicated exercises (the temporarily added one from location state and the one from the API)
        // we filter out the one from the API if it exists in the list
        const itemExerciseId = (state.item as UserCourseLibraryExercise).exercise_id;
        const filtered = sortedExercises.filter(ex => ex.exercise_id !== itemExerciseId);
        setFilteredExercises([state.item as UserCourseLibraryExercise, ...filtered]);
      } else {
        setFilteredExercises(sortedExercises);
      }
    }
  }, [allExercises, selectedTypeOption, initialSelectedIds, selectedSort]);

  const markAll = () => {
    if (allExercises && onSelectedExercisesChange) {
      onSelectedExercisesChange(
        filteredExercises.filter(exercise => !initialSelectedIds.includes(exercise.exercise_id))
      );
    }
  };

  const unmarkAll = () => {
    if (allExercises && onSelectedExercisesChange) {
      onSelectedExercisesChange([]);
    }
  };

  const paginationComponent = (
    <LibraryTablePagination
      count={totalCount}
      rowsPerPage={limit}
      page={!totalCount ? 0 : currentPage}
      onPageChange={(event, newPage) => {
        setCurrentPage(newPage);
      }}
      rowsPerPageOptions={[]}
    />
  );

  return (
    <Library>
      <LibraryHeader
        initialSelectedIds={initialSelectedIds}
        multiSelect={multiSelect}
        numberOfSelectedItems={selectedExercises.length}
        markAll={markAll}
        unmarkAll={unmarkAll}
        jointOptions={[
          { label: t("patients.pain_location.all"), value: "all" },
          ...sortAlphabetically({ list: targetJointOptions }),
        ]}
        onSortChange={sort => {
          setSelectedSort(sort);
        }}
        onPreviewJointChange={joint => {
          setPreviewJoint(joint);
        }}
        onSearchChange={_search => {
          setSearchTerm(_search);
        }}
        onSelectedTypeOptionChange={type => {
          setSelectedTypeOption(type);
        }}
        onEquipmentChange={selectedEquipment => {
          setEquipment(selectedEquipment);
        }}
        onMovementChange={selectedMovement => {
          setMovement(selectedMovement);
        }}
        onPositionChange={selectedPosition => {
          setPosition(selectedPosition);
        }}
        libraryListSettingsStorageKey={libraryListSettingsStorageKey}
        pagination={
          <LibraryTablePagination
            count={totalCount}
            rowsPerPage={limit}
            page={!totalCount ? 0 : currentPage}
            onPageChange={(event, newPage) => {
              setCurrentPage(newPage);
            }}
            rowsPerPageOptions={[]}
          />
        }
      />

      <SearchContainer>
        <SearchResults>
          {/* eslint-disable-next-line react/no-array-index-key */}
          {isLoading && [...Array(12)].map((_, index) => <LibraryItemSkeleton key={`exercise_skeleton_${index}`} />)}

          {!isLoading && allExercises?.length === 0 && <NoResultsBox>{t("usercourse.no_result")}</NoResultsBox>}

          {filteredExercises.map(item => {
            const checked = existsInSelectedExercises(selectedExercises, item);
            const alreadyAdded = initialSelectedIds.includes(item.exercise_id);

            return (
              <ExerciseLibraryItem
                key={`exercisepickeritem_${item.exercise_id}`}
                item={item}
                disabled={alreadyAdded}
                checked={checked || alreadyAdded}
                showAddButton={multiSelect}
                onClick={() => {
                  if (onClick) {
                    onClick(item.exercise_id, item);
                  }
                  if (onSelectedExercisesChange) {
                    if (multiSelect) {
                      if (checked) {
                        onSelectedExercisesChange(selectedExercises.filter(i => i.exercise_id !== item.exercise_id));
                      } else {
                        onSelectedExercisesChange([...selectedExercises, item]);
                      }
                    } else {
                      onSelectedExercisesChange([item]);
                    }
                  }
                }}
                onMoreInfoClick={onMoreInfoClick}
              />
            );
          })}
        </SearchResults>
      </SearchContainer>
      {paginationComponent}
    </Library>
  );
};

const Library = styled.div`
  padding-bottom: 16px;
`;
const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${props => props.theme.spacing.S_20};
`;
const SearchResults = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const NoResultsBox = styled.div`
  width: 100%;
  text-align: center;
  font-size: 16px;
  padding: 20px;
  background-color: #f8f8f8;
`;
